import {
  createRef, useMemo, useState, useEffect, useCallback
} from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { addMissingField, isIntentId, PAGE_SIZE, returnObject } from './productListHelper';

const listRef = createRef();

export const useListProduct = ({
  page = 1,
  id,
  isSharedList,
  isAddedMaterials,
  expandedGroups = new Set() // Track which groups are expanded
}) => {
  const { storeId, storeZip } = useStore({ varnish: true });

  // State for accumulating loaded products across batches
  const [accumulatedProducts, setAccumulatedProducts] = useState([]);

  const {
    data, loading, refetch, error, client
  } = useDataModel('listProductDetails', {
    variables: {
      id,
      storeId,
      sharedList: isSharedList
    },
    ssr: true,
    skip: !id,
    context: {
      withAuth: true
    }
  });

  const { listProductDetails: rawList } = returnObject(data);
  const list = useMemo(() => {
    return {
      ...rawList,
      groups: rawList?.groups?.map((__group) => ({
        ...__group,
        items: __group?.items?.map((__item) => ({
          ...__item,
          intent: decodeURIComponent(__item?.intent || '')
        })) || []
      })) || []
    };
  }, [rawList]);

  // work around name not being returned from privacy update
  const listChanged = (id && listRef?.current?.id !== id)
    || (list?.name && list.name !== listRef?.current?.name);

  if (!listRef.current || listChanged) {
    listRef.current = list;
  }

  // Extract all items from groups
  const results = useMemo(() => {
    if (!list?.groups) return [];
    return list.groups.flatMap((group) => group.items?.map((item) => ({
      ...item,
      groupId: group.groupId,
      groupName: group.groupName
    })) || []
    );
  }, [list?.groups]);

  const listProducts = useMemo(() => {
    if (!list?.groups) return [];
    return list.groups.flatMap((group) => group.items?.filter((item) => Boolean(item.itemId)) || []);
  }, [list?.groups]);

  // Filter items to only include those from expanded groups
  const expandedGroupItems = useMemo(() => {
    if (expandedGroups.size === 0) {
      // If no groups are tracked as expanded, assume all are expanded (backward compatibility)
      return results;
    }

    return results.filter((item) => {
      const groupKey = item.groupId || '__ungrouped__';
      const isExpanded = expandedGroups.has(groupKey);
      return isExpanded;
    });
  }, [results, expandedGroups]);

  const startIndex = isAddedMaterials ? 0 : (page - 1) * PAGE_SIZE;

  const itemIdsWithIntents = useMemo(() => {
    return expandedGroupItems
      .map((item) => ({
        id: item.itemId || item.intentId, isIntent: !item.itemId
      }));
  }, [expandedGroupItems]);

  // Use consistent pagination for both groups and regular lists
  // Always load 6 items at a time, but only for expanded groups
  const itemIds = useMemo(() => {
    return itemIdsWithIntents.filter((item) => !item.isIntent)
      .slice(startIndex, startIndex + PAGE_SIZE)
      .map((item) => (item.id))
      .filter(Boolean);
  }, [itemIdsWithIntents, startIndex]);

  const { data: productInfoData, loading: productInfoLoading, refetch: productRefetch } = useDataModel('products', {
    variables: {
      itemIds,
      storeId,
      zipCode: storeZip
    },
    skip: !itemIds.length
  });

  // Process product data from bulk 'products' API call
  const normalizedProducts = useMemo(() => {
    if (!productInfoData) return [];

    // Since we're only using 'products' call now, data is already an array
    const products = productInfoData.products || [];

    return products;
  }, [productInfoData, itemIds, page]);

  // Accumulate products across batches
  useEffect(() => {
    if (normalizedProducts.length > 0 && !productInfoLoading) {
      setAccumulatedProducts((prev) => {
        const newProducts = [...prev];

        // Add or update products
        normalizedProducts.forEach((product) => {
          const existingIndex = newProducts.findIndex((prod) => (
            (Boolean(prod.itemId) && prod.itemId === product.itemId)
            || (!prod.itemId && prod.intentId === product.intentId)
          ));
          if (existingIndex >= 0) {
            newProducts[existingIndex] = product;
          } else {
            newProducts.push(product);
          }
        });
        return newProducts;
      });
    }
  }, [normalizedProducts, productInfoLoading]);

  let productsDisplayed = useMemo(() => {
    const piData = accumulatedProducts.length > 0 ? accumulatedProducts : normalizedProducts;
    if (productInfoLoading && piData.length === 0) {
      return [];
    }

    // Hybrid approach: include both current page items AND any group-specific items
    const currentPageItemIds = itemIdsWithIntents
      .map((item) => item.id)
      .slice(startIndex, startIndex + PAGE_SIZE); // Items for current page (scroll pagination)
    // All loaded items (including group-specific)
    const allLoadedItemIds = piData.map((product) => product.itemId || product.intentId).filter(Boolean);

    // Combine current page items with any additional loaded items (deduped)
    const allRelevantItemIds = [...new Set([...currentPageItemIds, ...allLoadedItemIds])];

    // Get products for all relevant items
    const availableProducts = allRelevantItemIds
      .map((itemId) => piData.find(
        (prod) => prod.itemId === itemId || prod.intentId === itemId)
      )
      .filter(Boolean);

    return addMissingField({
      itemIds: allRelevantItemIds,
      searchData: availableProducts,
      listProducts: results
    });
  }, [itemIds, accumulatedProducts, results, productInfoLoading]);

  const clearListFromCache = async (listId) => {
    await client.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'listProductDetails',
      args: {
        id: listId,
        storeId
      }
    });
    await client.cache.gc();

    // Also clear accumulated products
    setAccumulatedProducts([]);
  };

  // Function to load specific item IDs on demand (for group expansion)
  const loadItemsOnDemand = useCallback(async (itemsToLoad) => {
    if (!itemsToLoad?.length) {
      return;
    }
    const specificItemIds = itemsToLoad.filter((__id) => !isIntentId(__id));
    if (specificItemIds.length === 0) {
      setAccumulatedProducts((prev) => {
        return [...prev, ...itemsToLoad.map((__id) => ({ itemId: null, intentId: __id }))];
      });
      return;
    }

    // Ensure specificItemIds is an array
    const itemIdsArray = Array.isArray(specificItemIds) ? specificItemIds : [specificItemIds];

    // Filter out items that are already loaded
    const unloadedItems = itemIdsArray.filter(
      (itemId) => !accumulatedProducts.some((prod) => prod.itemId === itemId)
    );

    if (unloadedItems.length === 0) {
      return;
    }

    // Batch items in groups of PAGE_SIZE (6) to respect the batching limit
    const batchSize = PAGE_SIZE;
    const batches = [];
    for (let i = 0; i < unloadedItems.length; i += batchSize) {
      batches.push(unloadedItems.slice(i, i + batchSize));
    }

    // Process batches sequentially to avoid overwhelming the server
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
      const batch = batches[batchIndex];

      try {
        // Use productRefetch (products query) for each batch
        // eslint-disable-next-line no-await-in-loop
        const batchData = await productRefetch({
          itemIds: batch,
          storeId,
          zipCode: storeZip
        });

        if (batchData?.data?.products && Array.isArray(batchData.data.products) && batchData.data.products.length > 0) {

          // Add the products to accumulated products
          setAccumulatedProducts((prev) => {
            const newProducts = [...prev];

            itemsToLoad.forEach((itemId) => {
              if (isIntentId(itemId)) {
                newProducts.push({ itemId: null, intentId: itemId });
              } else {
                const batchedProductData = batchData.data.products.find((prod) => prod.itemId === itemId);
                if (batchedProductData) {
                  const existingIndex = newProducts.findIndex((prod) => prod.itemId === itemId);
                  if (existingIndex >= 0) {
                    newProducts[existingIndex] = batchedProductData;
                  } else {
                    newProducts.push(batchedProductData);
                  }
                }
              }
            });
            return newProducts;
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn(`[Batch] ${batchIndex + 1} failed, no products returned`);
        }
      } catch (batchError) {
        // eslint-disable-next-line no-console
        console.error(`Error loading batch ${batchIndex + 1}:`, batchError);
      }
    }
  }, [accumulatedProducts, productRefetch, storeId, storeZip]);

  return {
    list,
    listProducts,
    showLoader: loading && !data,
    productInfoProducts: productsDisplayed,
    productInfoLoading,
    refetch,
    productRefetch,
    error,
    clearListFromCache,
    loadItemsOnDemand,
  };
};
